import { useMemo, useState } from 'react';

import { useParams } from 'react-router';

import { Alert } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormikProvider, useFormik } from 'formik';

import { CondicaoPagamentoAutoComplete, GatewayPagamentoAutoComplete } from 'components/autocompletes';
import { GatewayPagamento } from 'components/autocompletes/GatewayPagamentoAutoComplete';
import { BbarForm } from 'components/docked/BbarForm';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import { RequestListagem } from 'hooks/useRequestListagem';
import { ConfigPedidoCondicoesPagamentoGrid, ConfigPedidoHeaderActions, ConfigPedidoLojasModal } from 'pages/compra/configuracao-pedido/components';
import { ConfigPedidoListProvider } from 'pages/compra/configuracao-pedido/contexts';
import { useActionsCondicoesPagamento, useMutateCondicoesPagamento, useQueryCondicoesPagamento } from 'pages/compra/configuracao-pedido/hooks';
import { ConfigPedidoCondicoesPagamento } from 'pages/compra/configuracao-pedido/types';

const initialValues: ConfigPedidoCondicoesPagamento = {
    gatewayPagamento: [],
    condicaoPagamentoGeral: [],
    condicaoPagamentoLoja: [],
};

export function ConfigPedidoCondicoesPagamentoForm() {
    const { id } = useParams();
    const { onSubmit, isLoading } = useMutateCondicoesPagamento();
    const { data, isFetching } = useQueryCondicoesPagamento();

    const formik = useFormik({
        initialValues: data || initialValues,
        enableReinitialize: true,
        onSubmit,
    });

    const { values, setValues, submitForm } = formik;

    const { onGatewayChange, onGeneralConditionsChange, addLojas, removeLojas, updateLojaConditions } = useActionsCondicoesPagamento({
        values,
        setValues,
    });

    useLoaderContext(isFetching || isLoading);

    const [openLojasModal, setOpenLojasModal] = useState(false);

    const queryClient = useQueryClient();
    const queryData = queryClient.getQueriesData<RequestListagem<GatewayPagamento[]>>({ queryKey: ['gateway-pagamento'] });

    const gatewayPortalLojinha = useMemo(() => {
        if (queryData.length) {
            const [, data] = queryData[0];

            if (data?.data) {
                return data.data.find((gateway) => gateway.gatewayPagamentoEnum === 'PORTAL_LOJINHA');
            }
        }

        return null;
    }, [queryData]);

    return (
        <FormikProvider value={formik}>
            <ConfigPedidoLojasModal
                open={openLojasModal}
                setOpen={setOpenLojasModal}
                handleAdicionarLojas={addLojas}
                requestOptionsLojas={{
                    url: '/gestao/loja/findbyidsconfigpedido',
                    sort: [{ property: 'idLoja', direction: 'ASC' }],
                    filter: [{ property: 'idConfigPedido', operator: 'eq', value: id }],
                }}
            />

            <Block>
                <SubTitle label="Gateways de pagamento" />

                <GatewayPagamentoAutoComplete
                    multiple
                    label=""
                    size="small"
                    queryKey="gateway-pagamento"
                    value={values.gatewayPagamento}
                    onChange={(_, value: GatewayPagamento[]) => {
                        const hasPortalLojinha = value.some((item) => item.gatewayPagamentoEnum === 'PORTAL_LOJINHA');

                        if (value.length < 2 || (value.length === 2 && hasPortalLojinha)) {
                            onGatewayChange(value);
                        }
                    }}
                />

                {gatewayPortalLojinha && (
                    <Alert sx={{ py: 0, px: 1, mt: 2 }} severity="info">
                        Somente o gateway {gatewayPortalLojinha.dsGatewayPagamento} pode ser selecionado junto de outro gateway.
                    </Alert>
                )}
            </Block>

            <Block>
                <SubTitle label="Condições de pagamento (geral)" />

                <CondicaoPagamentoAutoComplete
                    multiple
                    label=""
                    size="small"
                    value={values.condicaoPagamentoGeral}
                    disabled={!values.gatewayPagamento.length}
                    onChange={(_, value) => onGeneralConditionsChange(value)}
                    requestOptions={{
                        nested: true,
                        url: '/gestao/condicaopagamento',
                        sort: [{ property: 'idCondicaoPagamento', direction: 'ASC' }],
                        filter: [
                            { property: 'fgAtivo', value: true, operator: 'eq' },
                            {
                                property: 'idGatewayPagamento',
                                operator: 'in',
                                value: values.gatewayPagamento.map((item) => item.idGatewayPagamento),
                            },
                        ],
                    }}
                />
            </Block>

            <ConfigPedidoListProvider
                onAdd={() => setOpenLojasModal(true)}
                onRemove={removeLojas}
                searchPlaceholder="Código, nome ou razão social..."
                addDisabled={!values.gatewayPagamento.length}
            >
                <Block>
                    <SubTitle label="Condições de pagamento (por loja)" />

                    <ConfigPedidoHeaderActions />

                    <ConfigPedidoCondicoesPagamentoGrid
                        onRemoveLoja={(cdLoja) => removeLojas(new Set([cdLoja]))}
                        onUpdateLoja={updateLojaConditions}
                    />
                </Block>
            </ConfigPedidoListProvider>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
